import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { LoginResponse, SessionUser } from "../contract.js";

/**
 * The session lives in localStorage rather than a cookie because the API is
 * pure Bearer-token: a reload must restore the session without a round trip,
 * and the refresh token is rotated on every use so a stale copy is worthless.
 */
const STORAGE_KEY = "ota-dashboard-session";

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
}

const SIGNED_OUT: AuthState = { accessToken: null, refreshToken: null, user: null };

function readPersistedSession(): AuthState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return SIGNED_OUT;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return SIGNED_OUT;
    const { accessToken, refreshToken, user } = parsed as Partial<AuthState>;
    if (typeof accessToken !== "string" || typeof refreshToken !== "string") return SIGNED_OUT;
    if (!user || typeof user.id !== "string" || typeof user.username !== "string") {
      return SIGNED_OUT;
    }
    return { accessToken, refreshToken, user };
  } catch {
    // Corrupt or foreign payload: start signed out instead of bricking the app.
    return SIGNED_OUT;
  }
}

export function persistSession(state: AuthState): void {
  if (!state.accessToken || !state.refreshToken || !state.user) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

const authSlice = createSlice({
  name: "auth",
  initialState: readPersistedSession(),
  reducers: {
    /** Login, silent refresh and password change all land here. */
    credentialsReceived(state, action: PayloadAction<LoginResponse>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.user = action.payload.user;
    },
    /** `GET /auth/me` is authoritative for role and mustChangePassword. */
    userRefreshed(state, action: PayloadAction<SessionUser>) {
      state.user = action.payload;
    },
    loggedOut() {
      return { ...SIGNED_OUT };
    },
  },
});

export const { credentialsReceived, userRefreshed, loggedOut } = authSlice.actions;
export const authReducer = authSlice.reducer;
